import { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Button, Card, CardContent, Typography } from '@mui/material';
import PropTypes from 'prop-types';

import { formatISODate } from '../../../../shared/utils';
import { PetMrrStateEnum } from '../../constants';
import MedicalRecordsReviewHistoryModal, {
  mrrHistoryListProps,
} from '../medical-records-review-history-modal';
import { medicalRequestRecordsInfoBannerStyle } from './medical-request-records-info-banner-style';

function getAttributedByState(state) {
  let title;
  let icon;
  let color;
  let backgroundColor;
  let border;
  switch (state) {
    case PetMrrStateEnum.NEEDS_REVIEW.id:
      title = 'Needs Review';
      icon = <WarningAmberIcon fontSize='medium' style={{ color: '#EF6C00' }} />;
      color = '#663C00';
      backgroundColor = '#FFF4E5';
      break;
    case PetMrrStateEnum.PARTIAL_HISTORY_REVIEWED.id:
      title = 'Partial History Reviewed';
      icon = <InfoOutlinedIcon fontSize='medium' style={{ color: '#0288D1' }} />;
      color = '#373F51';
      backgroundColor = '#EEF0FE';
      break;
    case PetMrrStateEnum.APPEAL_UNDER_REVIEW.id:
      title = 'Appeal Under Review';
      icon = <WarningAmberIcon fontSize='medium' style={{ color: '#EF6C00' }} />;
      color = '#663C00';
      backgroundColor = '#FFF4E5';
      break;
    case PetMrrStateEnum.FULL_HISTORY_REVIEWED.id:
      title = 'Full History Reviewed';
      color = '#1E4620';
      backgroundColor = '#EDF7ED';
      icon = (
        <CheckCircleOutlineIcon fontSize='medium' style={{ color: '#2E7D32' }} />
      );
      break;
    case PetMrrStateEnum.PREVISIT_MRR_TRIGGERED.id:
      title = 'Pre-visit MRR Triggered';
      color = '#373F51';
      backgroundColor = 'transparent';
      border = '1px solid #0288D1';
      icon = <InfoOutlinedIcon fontSize='medium' style={{ color: '#0288D1' }} />;
      break;
    case PetMrrStateEnum.PREVISIT_MRR_AWAITING_RECORDS.id:
      title = 'Pre-visit MRR Awaiting Records';
      color = '#373F51';
      backgroundColor = 'transparent';
      border = '1px solid #0288D1';
      icon = <InfoOutlinedIcon fontSize='medium' style={{ color: '#0288D1' }} />;
      break;
    case PetMrrStateEnum.PREVISIT_MRR_INFO_UPDATED.id:
      title = 'Pre-visit MRR Info Updated';
      color = '#373F51';
      backgroundColor = 'transparent';
      border = '1px solid #0288D1';
      icon = <InfoOutlinedIcon fontSize='medium' style={{ color: '#0288D1' }} />;
      break;
    case PetMrrStateEnum.MR_REVIEW_NOT_NEEDED.id:
      title = 'MR Review Not Needed';
      color = '#1E4620';
      backgroundColor = '#EDF7ED';
      icon = (
        <CheckCircleOutlineIcon fontSize='medium' style={{ color: '#2E7D32' }} />
      );
      break;
    default:
      title = 'Unknown or No Review';
      icon = <InfoOutlinedIcon fontSize='medium' style={{ color: '#0288D1' }} />;
      color = '#373F51';
      backgroundColor = 'transparent';
      border = '1px solid #0288D1';
  }
  return { title, icon, color, backgroundColor, border };
}

function MedicalRequestRecordsInfoBanner({
  currentOpsUserId,
  petId,
  onMrrHistoryUpdate,
  mrrHistoryList,
  claimSubmissionId = '',
}) {
  const classes = medicalRequestRecordsInfoBannerStyle();
  const latestMrrHistory = mrrHistoryList?.length
    ? mrrHistoryList[mrrHistoryList.length - 1]
    : {};
  const initiator =
    latestMrrHistory?.created_by == 'Ops User' && latestMrrHistory?.ops_user
      ? latestMrrHistory?.ops_user.name
      : latestMrrHistory?.created_by;
  const { title, icon, color, backgroundColor, border } = getAttributedByState(
    latestMrrHistory.new_mrr_state
  );
  const [openModal, setOpenModal] = useState(false);

  return (
    <>
      {openModal ? (
        <MedicalRecordsReviewHistoryModal
          currentState={latestMrrHistory.new_mrr_state}
          currentOpsUserId={currentOpsUserId}
          petId={petId}
          onClose={() => setOpenModal(false)}
          onSuccess={onMrrHistoryUpdate}
          claimSubmissionId={claimSubmissionId}
          mrrHistoryList={mrrHistoryList}
        />
      ) : null}
      <Card
        className={classes.card}
        style={{ backgroundColor, border: border || null }}
      >
        <CardContent className={classes.cardContent}>
          <div className={classes.infoContainer}>
            {icon}
            <Typography className={classes.content}>
              <span className={classes.bannerTitle} style={{ color }}>
                {title}
              </span>
              {initiator && latestMrrHistory?.created_at ? (
                <span
                  className={classes.bannerDescription}
                  style={{ color }}
                >{`History last updated ${formatISODate(
                  latestMrrHistory?.created_at,
                  {
                    isGenerated: true,
                  }
                )}, ${initiator}`}</span>
              ) : null}
            </Typography>
          </div>
          <Button
            color='primary'
            variant='contained'
            className={classes.logUpdateButton}
            style={{ color }}
            disableRipple
            onClick={() => setOpenModal(true)}
          >
            LOG/UPDATE
          </Button>
        </CardContent>
      </Card>
    </>
  );
}

MedicalRequestRecordsInfoBanner.propTypes = {
  currentOpsUserId: PropTypes.string,
  petId: PropTypes.string,
  onMrrHistoryUpdate: PropTypes.func,
  claimSubmissionId: PropTypes.string,
  mrrHistoryList: mrrHistoryListProps,
};

export default MedicalRequestRecordsInfoBanner;
